import React, { useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';
import {
    Activity,
    ArrowUpRight,
    BarChartLine,
    CashCoin,
    Cpu,
    People,
} from 'react-bootstrap-icons';

import {
    getPlatformOverview,
    getOrganizationStats,
    listSuperAdminOrganizations,
    listSuperAdminUsers,
    getModelUsageDistribution,
    getBillingTrend,
    getOrganizationComparison,
    getOrgAdminBillingTrend,
    type OrganizationStats,
    type PlatformOverviewStats,
    type SuperAdminOrganization,
    type SuperAdminUser,
    type ModelUsageDistribution,
    type BillingTrendResponse,
    type OrganizationComparisonResponse,
    type OrganizationComparisonMetric,
} from '../api/superadmin';
import apiClient, { getLLMModels, getOrganizationUsage, type LLMModelInfo } from '../api/config';
import { fetchPaymentMethod, fetchSubscriptionStatus } from '../api/stripe_billing';
import { fetchOrganizationOverview } from '../api/organizations';
import CustomDropdown, { type CustomDropdownOption } from '../components/CustomDropdown';
import { useAuth } from '../contexts/AuthContext';

import './SuperAdminAnalytics-new.css';

type RangeOption = 7 | 30 | 90 | 'all';
type SeriesKey = 'primary' | 'secondary';

interface StatCardData
{
    label: string;
    value: string;
    helper: string;
    delta?: string;
    icon: React.ReactNode;
    tone: 'cyan' | 'violet' | 'green' | 'blue';
}

interface OrgUsagePoint
{
    id: number | null;
    name: string;
    value: number;
}

interface ModelUsagePoint
{
    label: string;
    value: number;
    color: string;
}

interface TrendPoint
{
    label: string;
    primary: number;
    secondary: number;
}

interface AnalyticsState
{
    overview: PlatformOverviewStats | null;
    organizations: SuperAdminOrganization[];
    organizationStats: OrganizationStats[];
    users: SuperAdminUser[];
    availableModels: LLMModelInfo[];
    scopeUsageByUser?: Array<{ label: string; value: number }>;
    scopeUsageByModel?: Array<{ label: string; value: number }>;
    billingMessage?: string | null;
}

interface DynamicSeriesState
{
    modelUsage: ModelUsageDistribution | null;
    billingTrend: BillingTrendResponse | null;
    comparison: OrganizationComparisonResponse | null;
    loading: boolean;
    error: string | null;
}

const COMPARISON_METRIC_OPTIONS: Array<{ label: string; value: OrganizationComparisonMetric }> = [
    { label: 'Tokens', value: 'tokens' },
    { label: 'Jobs', value: 'jobs' },
    { label: 'LLM Charges', value: 'spend' },
    { label: 'Active Users', value: 'active_users' },
];

interface SuperAdminAnalyticsProps
{
    scope?: 'platform' | 'organization';
}

const RANGE_OPTIONS: Array<{ label: string; value: RangeOption }> = [
    { label: '7d', value: 7 },
    { label: '30d', value: 30 },
    { label: '90d', value: 90 },
    { label: 'All', value: 'all' },
];

const MODEL_COLORS = ['#2f66e3', '#7b4ce2', '#1ea97c', '#d88912', '#db4335'];

function formatCompactNumber(value: number): string
{
    if (!Number.isFinite(value))
    {
        return '0';
    }

    return new Intl.NumberFormat('en-US', {
        notation: value >= 10000 ? 'compact' : 'standard',
        maximumFractionDigits: value >= 10000 ? 1 : 0,
    }).format(value);
}

function formatCurrency(value: number): string
{
    const absolute = Math.abs(value || 0);
    const fractionDigits = absolute > 0 && absolute < 0.01
        ? 8
        : absolute >= 1000
            ? 0
            : 2;

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    }).format(value || 0);
}

function formatPercent(value: number): string
{
    const rounded = Math.round(value * 10) / 10;
    return `${rounded}%`;
}

function formatTokenValue(value: number): string
{
    return `${formatCompactNumber(value)} tokens`;
}

function toRangeDays(range: RangeOption): number | undefined
{
    return range === 'all' ? undefined : range;
}

function clampRatio(value: number): number
{
    if (!Number.isFinite(value))
    {
        return 0;
    }
    if (value < 0)
    {
        return 0;
    }
    if (value > 1)
    {
        return 1;
    }
    return value;
}

const EMPTY_OVERVIEW: PlatformOverviewStats = {
    users: { total: 0, active: 0, admins: 0, superusers: 0 },
    organizations: { total: 0 },
    teams: { total: 0 },
    jobs: { total: 0, by_status: {} },
    token_usage: { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
    billing: {
        total_charged_usd: 0,
        customer_charge_usd: 0,
        provider_cost_usd: 0,
        profit_usd: 0,
        payment_total_usd: 0,
        llm_usage_request_count: 0,
        transaction_count: 0,
    },
    window_days: null,
};

async function loadSuperAdminSingleOrgState(
    orgId: number,
    orgName: string,
    days: number | undefined,
    availableModels: LLMModelInfo[],
): Promise<AnalyticsState>
{
    const stats = await getOrganizationStats(orgId, days);

    return {
        overview: {
            users: {
                total: stats.users.total,
                active: stats.users.total,
                admins: stats.users.admins,
                superusers: 0,
            },
            organizations: { total: 1 },
            teams: { total: stats.teams.total },
            jobs: {
                total: stats.jobs.total,
                by_status: stats.jobs.by_status,
            },
            token_usage: {
                prompt_tokens: stats.token_usage.prompt_tokens,
                completion_tokens: stats.token_usage.completion_tokens,
                total_tokens: stats.token_usage.total_tokens,
            },
            billing: {
                total_charged_usd: stats.billing.total_charged_usd,
                customer_charge_usd: stats.billing.customer_charge_usd ?? stats.billing.total_charged_usd,
                provider_cost_usd: stats.billing.provider_cost_usd ?? 0,
                profit_usd: stats.billing.profit_usd ?? 0,
                payment_total_usd: stats.billing.payment_total_usd ?? 0,
                llm_usage_request_count: stats.billing.llm_usage_request_count ?? 0,
                transaction_count: stats.billing.transaction_count,
            },
            window_days: days ?? null,
        },
        organizations: [{
            id: orgId,
            name: orgName,
            created_at: '',
            user_count: stats.users.total,
            team_count: stats.teams.total,
        }],
        organizationStats: [stats],
        users: [],
        availableModels,
        scopeUsageByUser: stats.per_user_token_usage.map((u) => ({
            label: `User ${u.user_id}`,
            value: u.total_tokens,
        })),
        scopeUsageByModel: [],
    };
}

function buildStatCards(
    overview: PlatformOverviewStats,
    organizationStats: OrganizationStats[],
    showInternalBillingDetails: boolean,
): StatCardData[]
{
    const avgTokensPerUser = overview.users.total > 0
        ? overview.token_usage.total_tokens / overview.users.total
        : 0;
    const avgJobsPerOrg = overview.organizations.total > 0
        ? overview.jobs.total / overview.organizations.total
        : 0;
    const customerCharge = overview.billing.customer_charge_usd ?? overview.billing.total_charged_usd;
    const providerCost = overview.billing.provider_cost_usd ?? 0;
    const profit = overview.billing.profit_usd ?? customerCharge - providerCost;
    const avgChargePerOrg = overview.organizations.total > 0
        ? customerCharge / overview.organizations.total
        : 0;
    const orgCount = organizationStats.length || overview.organizations.total;

    return [
        {
            label: formatCompactNumber(overview.token_usage.total_tokens),
            value: formatCompactNumber(overview.token_usage.total_tokens),
            helper: `Avg. tokens / user ${Math.round(avgTokensPerUser)}`,
            icon: <Cpu size={16} />,
            tone: 'cyan',
        },
        {
            label: overview.organizations.total.toFixed(2),
            value: avgJobsPerOrg.toFixed(2),
            helper: `${formatCompactNumber(overview.jobs.total)} jobs across ${formatCompactNumber(orgCount)} orgs`,
            icon: <Activity size={16} />,
            tone: 'violet',
        },
        {
            label: formatCurrency(customerCharge),
            value: formatCurrency(customerCharge),
            helper: showInternalBillingDetails
                ? `Provider ${formatCurrency(providerCost)} · profit ${formatCurrency(profit)} · avg/org ${formatCurrency(avgChargePerOrg)}`
                : `Avg/org ${formatCurrency(avgChargePerOrg)}`,
            icon: <CashCoin size={16} />,
            tone: 'green',
        },
        {
            label: String(overview.users.active),
            value: String(overview.users.active),
            helper: `${formatCompactNumber(overview.users.total)} total users`,
            icon: <People size={16} />,
            tone: 'blue',
        },
    ];
}

function buildOrgUsageChart(organizationStats: OrganizationStats[]): OrgUsagePoint[]
{
    return organizationStats
        .map((item) => ({
            id: item.organization?.id ?? null,
            name: item.organization.name,
            value: item.token_usage.total_tokens,
        }))
        .sort((left, right) => right.value - left.value)
        .slice(0, 6);
}

function buildModelUsageChart(_overview: PlatformOverviewStats, _availableModels: LLMModelInfo[]): ModelUsagePoint[]
{
    // Returns empty array — model usage must come from the live distribution endpoint.
    return [];
}

function buildModelUsageFromDistribution(distribution: ModelUsageDistribution | null): ModelUsagePoint[]
{
    if (!distribution || !distribution.items || distribution.items.length === 0)
    {
        return [];
    }
    return distribution.items.slice(0, MODEL_COLORS.length).map((item, index) => ({
        label: item.model || `Model ${index + 1}`,
        value: Math.max(1, item.total_tokens),
        color: MODEL_COLORS[index % MODEL_COLORS.length],
    }));
}

function buildBillingTrendFromResponse(trend: BillingTrendResponse | null, includeSecondarySeries: boolean): TrendPoint[]
{
    if (!trend || !trend.items || trend.items.length === 0)
    {
        return [];
    }
    return trend.items.map((item) => ({
        label: item.label,
        primary: Number(item.customer_charge_usd ?? item.billed_usd ?? 0),
        secondary: includeSecondarySeries ? Number(item.provider_cost_usd ?? item.paid_usd ?? 0) : 0,
    }));
}

async function loadOrganizationAnalyticsState(
    activeOrganizationId: number,
    activeOrganizationName: string,
    range: RangeOption,
): Promise<AnalyticsState>
{
    const days = toRangeDays(range);

    const [overview, usage, subscriptionStatus, paymentMethod, testStats, availableModels] = await Promise.all([
        fetchOrganizationOverview(activeOrganizationId),
        getOrganizationUsage(days),
        fetchSubscriptionStatus(),
        fetchPaymentMethod(),
        apiClient.get<{
            total_test_generations: number;
            success_rate: number;
        }>('/api/dashboard/analytics/test-stats').then((response) => response.data),
        getLLMModels(),
    ]);

    const users = (overview.users?.items || []).map((user) => ({
        id: Number(user.id || 0),
        email: user.email,
        username: user.username || user.email,
        full_name: user.full_name || user.username || user.email,
        is_active: Boolean(user.is_active),
        is_admin: String(user.role || '').toLowerCase() === 'admin',
        is_superuser: false,
        has_temporary_password: false,
        organization_id: activeOrganizationId,
        organization_name: activeOrganizationName,
        created_at: user.created_at || new Date().toISOString(),
    }));

    const adminCount = users.filter((user) => user.is_admin).length;
    const totalJobs = Number(testStats.total_test_generations || overview.summary?.job_count || 0);
    const completedJobs = Math.round(totalJobs * ((testStats.success_rate || 0) / 100));
    const failedJobs = Math.max(totalJobs - completedJobs, 0);
    const customerChargeUsd = Number(usage.customer_charge_usd ?? subscriptionStatus.platform_metered_charge_usd ?? 0);
    const providerCostUsd = Number(usage.provider_cost_usd ?? 0);
    const profitUsd = Number(usage.profit_usd ?? customerChargeUsd - providerCostUsd);

    return {
        overview: {
            users: {
                total: Number(overview.summary?.user_count || 0),
                active: Number(overview.summary?.active_user_count || 0),
                admins: adminCount,
                superusers: 0,
            },
            organizations: {
                total: 1,
            },
            teams: {
                total: Number(overview.summary?.team_count || 0),
            },
            jobs: {
                total: totalJobs,
                by_status: {
                    completed: completedJobs,
                    failed: failedJobs,
                },
            },
            token_usage: {
                prompt_tokens: Number(usage.prompt_tokens || 0),
                completion_tokens: Number(usage.completion_tokens || 0),
                total_tokens: Number(usage.total_tokens || 0),
            },
            billing: {
                total_charged_usd: customerChargeUsd,
                customer_charge_usd: customerChargeUsd,
                provider_cost_usd: providerCostUsd,
                profit_usd: profitUsd,
                payment_total_usd: 0,
                llm_usage_request_count: Number(usage.job_count || 0),
                transaction_count: 0,
            },
            window_days: days ?? null,
        },
        organizations: [{
            id: activeOrganizationId,
            name: activeOrganizationName,
            created_at: overview.created_at || new Date().toISOString(),
            user_count: Number(overview.summary?.user_count || 0),
            team_count: Number(overview.summary?.team_count || 0),
        }],
        organizationStats: [{
            organization: {
                id: activeOrganizationId,
                name: activeOrganizationName,
            },
            users: {
                total: Number(overview.summary?.user_count || 0),
                admins: adminCount,
            },
            teams: {
                total: Number(overview.summary?.team_count || 0),
                details: [],
            },
            jobs: {
                total: totalJobs,
                by_status: {
                    completed: completedJobs,
                    failed: failedJobs,
                },
            },
            token_usage: {
                prompt_tokens: Number(usage.prompt_tokens || 0),
                completion_tokens: Number(usage.completion_tokens || 0),
                total_tokens: Number(usage.total_tokens || 0),
            },
            per_user_token_usage: (usage.by_user || [])
                .filter((item) => item.user_id != null)
                .map((item) => ({
                    user_id: Number(item.user_id),
                    total_tokens: Number(item.total_tokens || 0),
                })),
            billing: {
                stripe_customer_id: paymentMethod.customer_id || null,
                has_payment_method: Boolean(paymentMethod.has_payment_method),
                price_per_1k_tokens: 0,
                free_token_quota: Number(subscriptionStatus.free_tokens_allocated || 0),
                total_charged_usd: customerChargeUsd,
                customer_charge_usd: customerChargeUsd,
                provider_cost_usd: providerCostUsd,
                profit_usd: profitUsd,
                payment_total_usd: 0,
                llm_usage_request_count: Number(usage.job_count || 0),
                transaction_count: 0,
            },
            window_days: days ?? null,
        }],
        users,
        availableModels,
        scopeUsageByUser: (usage.by_user || []).map((item) =>
        {
            const matched = users.find((u) => u.email === item.email || (item.user_id != null && u.id === item.user_id));
            return {
                label: matched?.full_name || item.email || 'unknown',
                value: Number(item.total_tokens || 0),
            };
        }),
        scopeUsageByModel: (usage.by_model || []).map((item) => ({
            label: item.model || 'unknown',
            value: Number(item.total_tokens || 0),
        })),
        billingMessage: subscriptionStatus.message || null,
    };
}

function buildUserGrowth(users: SuperAdminUser[], overview: PlatformOverviewStats): TrendPoint[]
{
    const labels = ['W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7'];
    const totalUsers = overview.users.total;
    const weeklyNewUsers = new Array(labels.length).fill(0);

    const now = Date.now();
    for (const user of users)
    {
        const createdAt = new Date(user.created_at).getTime();
        if (!Number.isFinite(createdAt))
        {
            continue;
        }

        const ageInDays = Math.floor((now - createdAt) / (24 * 60 * 60 * 1000));
        const bucketIndex = labels.length - 1 - Math.floor(ageInDays / 7);
        if (bucketIndex >= 0 && bucketIndex < labels.length)
        {
            weeklyNewUsers[bucketIndex] += 1;
        }
    }

    // Cumulative active users built from total minus those created after each week
    let runningTotal = totalUsers;

    return labels.map((label, index) =>
    {
        const reverseIndex = labels.length - 1 - index;
        const newUsers = weeklyNewUsers[reverseIndex] || 0;
        const growth = Math.max(runningTotal, 0);
        runningTotal = Math.max(runningTotal - newUsers, 0);
        return {
            label,
            primary: growth,
            secondary: weeklyNewUsers[index] || 0,
        };
    });
}

function polylinePoints(points: TrendPoint[], key: 'primary' | 'secondary', width: number, height: number, padding: number): string
{
    if (points.length === 0)
    {
        return '';
    }

    const values = points.map((point) => point[key]);
    const maxValue = Math.max(...values, 1);
    const usableWidth = width - padding * 2;
    const usableHeight = height - padding * 2;

    return points.map((point, index) =>
    {
        const x = padding + (usableWidth * index) / Math.max(points.length - 1, 1);
        const y = height - padding - (usableHeight * point[key]) / maxValue;
        return `${x},${y}`;
    }).join(' ');
}

function areaPath(points: TrendPoint[], key: 'primary' | 'secondary', width: number, height: number, padding: number): string
{
    const line = polylinePoints(points, key, width, height, padding);
    if (!line)
    {
        return '';
    }

    const coordinates = line.split(' ');
    const first = coordinates[0]?.split(',') || [String(padding), String(height - padding)];
    const last = coordinates[coordinates.length - 1]?.split(',') || [String(width - padding), String(height - padding)];

    return `M ${first[0]} ${height - padding} L ${line.replace(/,/g, ' ')} L ${last[0]} ${height - padding} Z`;
}

function totalOfModelUsage(points: ModelUsagePoint[]): number
{
    return points.reduce((sum, point) => sum + point.value, 0);
}

function renderPieSegments(
    points: ModelUsagePoint[],
    selectedLabel: string | null,
    onSelect: (label: string) => void,
): React.ReactNode
{
    const total = totalOfModelUsage(points);
    let accumulated = 0;

    return points.map((point) =>
    {
        const startAngle = (accumulated / total) * Math.PI * 2 - Math.PI / 2;
        accumulated += point.value;
        const endAngle = (accumulated / total) * Math.PI * 2 - Math.PI / 2;
        const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
        const x1 = 80 + Math.cos(startAngle) * 58;
        const y1 = 80 + Math.sin(startAngle) * 58;
        const x2 = 80 + Math.cos(endAngle) * 58;
        const y2 = 80 + Math.sin(endAngle) * 58;

        const path = [
            `M 80 80`,
            `L ${x1} ${y1}`,
            `A 58 58 0 ${largeArc} 1 ${x2} ${y2}`,
            'Z',
        ].join(' ');

        const isSelected = selectedLabel === point.label;

        return (
            <path
                key={point.label}
                d={path}
                fill={point.color}
                className={`sa-pie-segment ${isSelected ? 'sa-pie-segment-selected' : ''}`}
                aria-label={`${point.label} ${formatPercent((point.value / total) * 100)}`}
                onClick={() => onSelect(point.label)}
                onKeyDown={(event) =>
                {
                    if (event.key === 'Enter' || event.key === ' ')
                    {
                        event.preventDefault();
                        onSelect(point.label);
                    }
                }}
                tabIndex={0}
                role="button"
            />
        );
    });
}

function SuperAdminAnalytics({ scope = 'platform' }: SuperAdminAnalyticsProps): React.ReactElement
{
    const { user } = useAuth();
    const [range, setRange] = useState<RangeOption>(30);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedOrganizationName, setSelectedOrganizationName] = useState<string | null>(null);
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
    // Super-admin org filter: null = all organizations, number = single org
    const [filterOrgId, setFilterOrgId] = useState<number | null>(null);
    const [filterOrgName, setFilterOrgName] = useState<string>('');
    // Persisted org list for the super-admin dropdown (not cleared on filter change)
    const [allOrgs, setAllOrgs] = useState<SuperAdminOrganization[]>([]);
    const [selectedModelLabel, setSelectedModelLabel] = useState<string | null>(null);
    const [selectedBillingLabel, setSelectedBillingLabel] = useState<string | null>(null);
    const [selectedGrowthLabel, setSelectedGrowthLabel] = useState<string | null>(null);
    const [selectedBillingSeries, setSelectedBillingSeries] = useState<SeriesKey>('primary');
    const [selectedGrowthSeries, setSelectedGrowthSeries] = useState<SeriesKey>('primary');
    const [comparisonMetric, setComparisonMetric] = useState<OrganizationComparisonMetric>('tokens');
    const [dynamicSeries, setDynamicSeries] = useState<DynamicSeriesState>({
        modelUsage: null,
        billingTrend: null,
        comparison: null,
        loading: false,
        error: null,
    });
    const [state, setState] = useState<AnalyticsState>({
        overview: null,
        organizations: [],
        organizationStats: [],
        users: [],
        availableModels: [],
        scopeUsageByUser: [],
        scopeUsageByModel: [],
    });
    const activeOrganizationId = Number(user?.active_organization_id || user?.organization_id || 0) || 0;
    const activeOrganizationName = (user?.organization_name || '').trim() || 'Active Organization';
    const isOrganizationScope = scope === 'organization' || activeOrganizationId > 0;
    const dynamicOrgId = isOrganizationScope
        ? activeOrganizationId
        : (filterOrgId ?? selectedOrgId ?? undefined);

    useEffect(() =>
    {
        let cancelled = false;

        async function load(): Promise<void>
        {
            setLoading(true);
            setError(null);

            const days = toRangeDays(range);

            try
            {
                if (isOrganizationScope)
                {
                    if (!activeOrganizationId)
                    {
                        if (!cancelled)
                        {
                            setError('Select an active organization to view analytics.');
                            setState({
                                overview: null,
                                organizations: [],
                                organizationStats: [],
                                users: [],
                                availableModels: [],
                                scopeUsageByUser: [],
                                scopeUsageByModel: [],
                            });
                        }
                        return;
                    }

                    const organizationState = await loadOrganizationAnalyticsState(activeOrganizationId, activeOrganizationName, range);
                    if (!cancelled)
                    {
                        setState(organizationState);
                    }
                    return;
                }

                // Super admin: single org filter
                if (filterOrgId !== null)
                {
                    const modelsResult = await getLLMModels().catch(() => [] as LLMModelInfo[]);
                    const singleOrgState = await loadSuperAdminSingleOrgState(filterOrgId, filterOrgName, days, modelsResult);
                    if (!cancelled)
                    {
                        setState(singleOrgState);
                    }
                    return;
                }

                // Super admin: platform-wide load
                const [overviewResult, orgResult, usersResult, modelsResult] = await Promise.allSettled([
                    getPlatformOverview(days),
                    listSuperAdminOrganizations({ page: 1, page_size: 50 }),
                    listSuperAdminUsers({ page: 1, page_size: 150 }),
                    getLLMModels(),
                ]);

                const organizations = orgResult.status === 'fulfilled'
                    ? orgResult.value.items.slice(0, 6)
                    : [];

                // Persist the full org list for the filter dropdown
                if (orgResult.status === 'fulfilled' && !cancelled)
                {
                    setAllOrgs(orgResult.value.items);
                }

                const organizationStatResults = await Promise.allSettled(
                    organizations.map((organization) => getOrganizationStats(organization.id, days)),
                );

                const organizationStats = organizationStatResults
                    .filter((result): result is PromiseFulfilledResult<OrganizationStats> => result.status === 'fulfilled')
                    .map((result) => result.value);

                const overview = overviewResult.status === 'fulfilled'
                    ? overviewResult.value
                    : null;
                const users = usersResult.status === 'fulfilled'
                    ? usersResult.value.items
                    : [];
                const availableModels = modelsResult.status === 'fulfilled'
                    ? modelsResult.value
                    : [];

                if (!cancelled)
                {
                    setState({
                        overview,
                        organizations,
                        organizationStats,
                        users,
                        availableModels,
                        scopeUsageByUser: [],
                        scopeUsageByModel: [],
                    });

                    if (overviewResult.status !== 'fulfilled' || orgResult.status !== 'fulfilled')
                    {
                        setError('Some analytics data could not be loaded. Please try refreshing.');
                    }
                }
            }
            catch (loadError)
            {
                if (!cancelled)
                {
                    setState({
                        overview: null,
                        organizations: [],
                        organizationStats: [],
                        users: [],
                        availableModels: [],
                        scopeUsageByUser: [],
                        scopeUsageByModel: [],
                    });
                    setError(loadError instanceof Error
                        ? loadError.message
                        : 'Failed to load analytics data. Please try again.');
                }
            }
            finally
            {
                if (!cancelled)
                {
                    setLoading(false);
                }
            }
        }

        void load();
        return () =>
        {
            cancelled = true;
        };
    }, [activeOrganizationId, activeOrganizationName, isOrganizationScope, range, filterOrgId, filterOrgName]);

    // For organization-scoped views the selection is locked to the active organization.
    useEffect(() =>
    {
        if (isOrganizationScope)
        {
            setSelectedOrgId(activeOrganizationId || null);
        }
    }, [activeOrganizationId, isOrganizationScope]);

    // Fetch dynamic model usage + billing trend whenever the selected organization or range changes.
    useEffect(() =>
    {
        let cancelled = false;
        const days = toRangeDays(range);

        async function loadDynamic(): Promise<void>
        {
            setDynamicSeries((previous) => ({ ...previous, loading: true, error: null }));
            try
            {
                if (isOrganizationScope)
                {
                    const billingResult = await Promise.allSettled([getOrgAdminBillingTrend(6)]);

                    if (cancelled)
                    {
                        return;
                    }

                    setDynamicSeries((previous) => ({
                        ...previous,
                        loading: false,
                        modelUsage: null,
                        billingTrend: billingResult[0].status === 'fulfilled' ? billingResult[0].value : null,
                        error: billingResult[0].status === 'rejected'
                            ? 'Some dynamic charts could not load. Showing best available data.'
                            : null,
                    }));
                    return;
                }

                const [modelResult, billingResult] = await Promise.allSettled([
                    getModelUsageDistribution({ organization_id: dynamicOrgId || undefined, days, limit: 8 }),
                    getBillingTrend({ organization_id: dynamicOrgId || undefined, months: 6 }),
                ]);

                if (cancelled)
                {
                    return;
                }

                setDynamicSeries((previous) => ({
                    ...previous,
                    loading: false,
                    modelUsage: modelResult.status === 'fulfilled' ? modelResult.value : null,
                    billingTrend: billingResult.status === 'fulfilled' ? billingResult.value : null,
                    error: (modelResult.status === 'rejected' || billingResult.status === 'rejected')
                        ? 'Some dynamic charts could not load. Showing best available data.'
                        : null,
                }));
            }
            catch (loadError)
            {
                if (!cancelled)
                {
                    setDynamicSeries((previous) => ({
                        ...previous,
                        loading: false,
                        error: loadError instanceof Error ? loadError.message : 'Failed to load dynamic charts.',
                    }));
                }
            }
        }

        if (isOrganizationScope && !activeOrganizationId)
        {
            return () => { cancelled = true; };
        }

        void loadDynamic();
        return () => { cancelled = true; };
    }, [activeOrganizationId, dynamicOrgId, isOrganizationScope, range]);

    // Fetch comparison rankings (platform scope only).
    useEffect(() =>
    {
        if (isOrganizationScope)
        {
            return;
        }
        let cancelled = false;
        const days = toRangeDays(range);

        async function loadComparison(): Promise<void>
        {
            try
            {
                const result = await getOrganizationComparison({ metric: comparisonMetric, days, limit: 6 });
                if (!cancelled)
                {
                    setDynamicSeries((previous) => ({ ...previous, comparison: result }));
                }
            }
            catch
            {
                if (!cancelled)
                {
                    setDynamicSeries((previous) => ({ ...previous, comparison: null }));
                }
            }
        }

        void loadComparison();
        return () => { cancelled = true; };
    }, [comparisonMetric, isOrganizationScope, range]);

    const overview = state.overview ?? EMPTY_OVERVIEW;
    const organizationStats = state.organizationStats;

    const statCards = useMemo(
        () => buildStatCards(overview, organizationStats, !isOrganizationScope),
        [overview, organizationStats, isOrganizationScope],
    );
    const orgUsagePoints = useMemo(
        () => isOrganizationScope && (state.scopeUsageByUser || []).length > 0
            ? (state.scopeUsageByUser || []).map((point) => ({ id: null as number | null, name: point.label, value: point.value }))
            : filterOrgId !== null && (state.scopeUsageByUser || []).length > 0
                ? (state.scopeUsageByUser || []).map((point) => ({ id: null as number | null, name: point.label, value: point.value }))
                : buildOrgUsageChart(organizationStats),
        [filterOrgId, isOrganizationScope, organizationStats, state.scopeUsageByUser],
    );
    const modelUsagePoints = useMemo(
        () =>
        {
            const dynamic = buildModelUsageFromDistribution(dynamicSeries.modelUsage);
            if (dynamic.length > 0)
            {
                return dynamic;
            }
            if ((state.scopeUsageByModel || []).length > 0)
            {
                return (state.scopeUsageByModel || []).slice(0, MODEL_COLORS.length).map((point, index) => ({
                    label: point.label,
                    value: Math.max(1, point.value),
                    color: MODEL_COLORS[index % MODEL_COLORS.length],
                }));
            }
            return buildModelUsageChart(overview, state.availableModels);
        },
        [dynamicSeries.modelUsage, overview, state.availableModels, state.scopeUsageByModel],
    );
    const billingTrend = useMemo(
        () => buildBillingTrendFromResponse(dynamicSeries.billingTrend, !isOrganizationScope),
        [dynamicSeries.billingTrend, isOrganizationScope],
    );
    const userGrowth = useMemo(
        () => buildUserGrowth(state.users, overview),
        [state.users, overview],
    );

    const maxOrgValue = Math.max(...orgUsagePoints.map((item) => item.value), 1);
    const billingPrimaryPoints = polylinePoints(billingTrend, 'primary', 560, 220, 24);
    const billingSecondaryPoints = polylinePoints(billingTrend, 'secondary', 560, 220, 24);
    const billingArea = areaPath(billingTrend, 'primary', 560, 220, 24);
    const growthPrimaryPoints = polylinePoints(userGrowth, 'primary', 560, 220, 24);
    const growthSecondaryPoints = polylinePoints(userGrowth, 'secondary', 560, 220, 24);
    const growthArea = areaPath(userGrowth, 'primary', 560, 220, 24);
    const totalModelUsage = totalOfModelUsage(modelUsagePoints);
    const totalOrganizationUsage = orgUsagePoints.reduce((sum, point) => sum + point.value, 0);

    useEffect(() =>
    {
        if (orgUsagePoints.length === 0)
        {
            setSelectedOrganizationName(null);
            return;
        }
        if (!selectedOrganizationName || !orgUsagePoints.some((point) => point.name === selectedOrganizationName))
        {
            setSelectedOrganizationName(orgUsagePoints[0].name);
            const firstId = orgUsagePoints[0].id;
            if (!isOrganizationScope && firstId != null && selectedOrgId == null)
            {
                setSelectedOrgId(firstId);
            }
        }
    }, [isOrganizationScope, orgUsagePoints, selectedOrgId, selectedOrganizationName]);

    useEffect(() =>
    {
        if (modelUsagePoints.length === 0)
        {
            setSelectedModelLabel(null);
            return;
        }
        if (!selectedModelLabel || !modelUsagePoints.some((point) => point.label === selectedModelLabel))
        {
            setSelectedModelLabel(modelUsagePoints[0].label);
        }
    }, [modelUsagePoints, selectedModelLabel]);

    useEffect(() =>
    {
        if (billingTrend.length === 0)
        {
            setSelectedBillingLabel(null);
            return;
        }
        if (!selectedBillingLabel || !billingTrend.some((point) => point.label === selectedBillingLabel))
        {
            // The trend is chronological, so its final point is the current month.
            setSelectedBillingLabel(billingTrend[billingTrend.length - 1].label);
        }
    }, [billingTrend, selectedBillingLabel]);

    useEffect(() =>
    {
        if (isOrganizationScope && selectedBillingSeries !== 'primary')
        {
            setSelectedBillingSeries('primary');
        }
    }, [isOrganizationScope, selectedBillingSeries]);

    useEffect(() =>
    {
        if (userGrowth.length === 0)
        {
            setSelectedGrowthLabel(null);
            return;
        }
        if (!selectedGrowthLabel || !userGrowth.some((point) => point.label === selectedGrowthLabel))
        {
            setSelectedGrowthLabel(userGrowth[0].label);
        }
    }, [userGrowth, selectedGrowthLabel]);

    const selectedOrganization = orgUsagePoints.find((point) => point.name === selectedOrganizationName) ?? orgUsagePoints[0];
    const selectedModel = modelUsagePoints.find((point) => point.label === selectedModelLabel) ?? modelUsagePoints[0];
    const selectedBillingPoint = billingTrend.find((point) => point.label === selectedBillingLabel) ?? billingTrend[0];
    const selectedGrowthPoint = userGrowth.find((point) => point.label === selectedGrowthLabel) ?? userGrowth[0];
    const showInternalBillingDetails = !isOrganizationScope;
    const breadcrumbLabel = isOrganizationScope ? 'Organization Admin' : 'Super Admin';
    const breadcrumbValue = isOrganizationScope
        ? activeOrganizationName
        : (filterOrgId !== null ? filterOrgName : 'Portal');
    const subtitle = isOrganizationScope
        ? 'Active organization statistics and insights'
        : filterOrgId !== null
            ? `Statistics and insights for ${filterOrgName}`
            : 'Platform-wide statistics and insights';
    const usageChartTitle = (isOrganizationScope || filterOrgId !== null) ? 'Token Usage by User' : 'Token Usage by Organization';
    const usageSelectionLabel = isOrganizationScope
        ? `${formatPercent((selectedOrganization?.value || 0) / Math.max(totalOrganizationUsage, 1) * 100)} of visible user usage`
        : `${formatPercent((selectedOrganization?.value || 0) / Math.max(totalOrganizationUsage, 1) * 100)} of visible organization usage`;
    const caption = isOrganizationScope
        ? 'Dashboard values are derived from the active organization only.'
        : filterOrgId !== null
            ? `Dashboard values are scoped to ${filterOrgName}.`
            : 'Dashboard values are derived from the platform overview and super-admin organization data.';

    return (
        <div className="sa-analytics-page">
            <div className="sa-analytics-breadcrumbs">{breadcrumbLabel} <span>/</span> {breadcrumbValue}</div>

            <div className="sa-analytics-header-row">
                <div>
                    <h1 className="sa-analytics-title">Analytics</h1>
                    <p className="sa-analytics-subtitle">{subtitle}</p>
                </div>

                <div className="sa-analytics-controls">
                    {!isOrganizationScope && (
                        <CustomDropdown
                            className="sa-org-filter-dropdown"
                            placeholder="All Organizations"
                            value={filterOrgId?.toString() ?? ''}
                            options={[
                                { value: '', label: 'All Organizations' } as CustomDropdownOption,
                                ...allOrgs.map((org) => ({ value: org.id.toString(), label: org.name })),
                            ]}
                            onChange={(val) =>
                            {
                                if (val === '')
                                {
                                    setFilterOrgId(null);
                                    setFilterOrgName('');
                                }
                                else
                                {
                                    const orgId = Number(val);
                                    const org = allOrgs.find((o) => o.id === orgId);
                                    setFilterOrgId(orgId);
                                    setFilterOrgName(org?.name ?? `Organization ${orgId}`);
                                }
                            }}
                        />
                    )}

                <div className="sa-analytics-range-switcher" aria-label="Analytics date range">
                    {RANGE_OPTIONS.map((option) => (
                        <button
                            key={option.label}
                            type="button"
                            className={`sa-range-pill ${range === option.value ? 'sa-range-pill-active' : ''}`}
                            onClick={() => setRange(option.value)}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                </div>
            </div>

            {error && <Alert variant="warning" className="sa-analytics-alert">{error}</Alert>}
            {!error && state.billingMessage && (
                <Alert variant="info" className="sa-analytics-alert">{state.billingMessage}</Alert>
            )}

            {loading ? (
                <div className="sa-analytics-loading">
                    <Spinner animation="border" />
                </div>
            ) : (
                <>
                    <section className="sa-stats-grid">
                        {statCards.map((card) => (
                            <article key={card.helper} className="sa-stat-card">
                                <div className={`sa-stat-icon sa-stat-icon-${card.tone}`}>{card.icon}</div>
                                {card.delta && (
                                    <div className="sa-stat-delta">
                                        <ArrowUpRight size={11} />
                                        {card.delta}
                                    </div>
                                )}
                                <div className="sa-stat-value">{card.value}</div>
                                <div className="sa-stat-helper">{card.helper}</div>
                            </article>
                        ))}
                    </section>

                    <section className="sa-chart-grid sa-chart-grid-top">
                        <article className="sa-chart-card">
                            <div className="sa-chart-header">
                                <h2>{usageChartTitle}</h2>
                            </div>
                            {selectedOrganization && (
                                <button type="button" className="sa-chart-selection-card sa-chart-selection-card-left">
                                    <strong>{selectedOrganization.name}</strong>
                                    <span>{formatTokenValue(selectedOrganization.value)}</span>
                                    <small>{usageSelectionLabel}</small>
                                </button>
                            )}
                            <div className="sa-bar-chart">
                                {orgUsagePoints.map((point) => (
                                    <button
                                        key={point.name}
                                        type="button"
                                        className={`sa-bar-row ${selectedOrganization?.name === point.name ? 'sa-bar-row-selected' : ''}`}
                                        onClick={() =>
                                        {
                                            setSelectedOrganizationName(point.name);
                                            if (!isOrganizationScope && point.id != null)
                                            {
                                                setSelectedOrgId(point.id);
                                            }
                                        }}
                                    >
                                        <div className="sa-bar-label">{point.name}</div>
                                        <svg className="sa-bar-track" viewBox="0 0 100 14" preserveAspectRatio="none" role="presentation">
                                            <rect className="sa-bar-track-bg" x="0" y="0" width="100" height="14" rx="4" ry="4" />
                                            <rect
                                                className={`sa-bar-fill ${selectedOrganization?.name === point.name ? 'sa-bar-fill-selected' : ''}`}
                                                x="0"
                                                y="0"
                                                width={clampRatio(point.value / maxOrgValue) * 100}
                                                height="14"
                                                rx="4"
                                                ry="4"
                                            />
                                        </svg>
                                        <div className="sa-bar-value">{formatCompactNumber(point.value)}</div>
                                    </button>
                                ))}
                            </div>
                        </article>

                        <article className="sa-chart-card">
                            <div className="sa-chart-header">
                                <h2>Model Usage Distribution</h2>
                            </div>
                            {selectedModel && (
                                <div className="sa-chart-selection-card">
                                    <strong>{selectedModel.label}</strong>
                                    <span>{formatPercent((selectedModel.value / Math.max(totalModelUsage, 1)) * 100)}</span>
                                    <small>{formatTokenValue(selectedModel.value)}</small>
                                </div>
                            )}
                            <div className="sa-pie-layout">
                                <div className="sa-pie-wrap">
                                    <svg className="sa-pie-chart" viewBox="0 0 160 160" role="img" aria-label="Model usage distribution chart">
                                        {renderPieSegments(modelUsagePoints, selectedModel?.label ?? null, setSelectedModelLabel)}
                                        <circle cx="80" cy="80" r="26" fill="#ffffff" />
                                    </svg>
                                </div>
                                <div className="sa-legend-list">
                                    {modelUsagePoints.map((point, index) => (
                                        <button
                                            key={point.label}
                                            type="button"
                                            className={`sa-legend-row ${selectedModel?.label === point.label ? 'sa-legend-row-selected' : ''}`}
                                            onClick={() => setSelectedModelLabel(point.label)}
                                        >
                                            <div className="sa-legend-key">
                                                <span className={`sa-legend-dot sa-legend-dot-${index + 1}`} />
                                                <span>{point.label}</span>
                                            </div>
                                            <span>{formatPercent(point.value / totalModelUsage * 100)}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </article>
                    </section>

                    <section className="sa-chart-grid">
                        <article className="sa-chart-card">
                            <div className="sa-chart-header">
                                <h2>Monthly LLM Cost (USD)</h2>
                            </div>
                            {selectedBillingPoint && (
                                <div className="sa-chart-selection-card">
                                    <strong>{selectedBillingPoint.label}</strong>
                                    <span>
                                        {showInternalBillingDetails
                                            ? (selectedBillingSeries === 'primary'
                                                ? `Customer charge ${formatCurrency(selectedBillingPoint.primary)}`
                                                : `Provider cost ${formatCurrency(selectedBillingPoint.secondary)}`)
                                            : `LLM cost ${formatCurrency(selectedBillingPoint.primary)}`}
                                    </span>
                                    <small>
                                        {showInternalBillingDetails
                                            ? (selectedBillingSeries === 'primary'
                                                ? `Provider cost ${formatCurrency(selectedBillingPoint.secondary)}`
                                                : `Customer charge ${formatCurrency(selectedBillingPoint.primary)}`)
                                            : 'Customer-facing billed LLM cost'}
                                    </small>
                                </div>
                            )}
                            <div className="sa-line-chart-wrap">
                                <svg
                                    className="sa-line-chart"
                                    viewBox="0 0 560 220"
                                    role="img"
                                    aria-label={showInternalBillingDetails ? 'Monthly LLM customer charge and provider cost chart' : 'Monthly billed LLM cost chart'}
                                >
                                    <defs>
                                        <linearGradient id="saBillingArea" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="rgba(47, 102, 227, 0.18)" />
                                            <stop offset="100%" stopColor="rgba(47, 102, 227, 0.02)" />
                                        </linearGradient>
                                    </defs>
                                    {[0, 1, 2, 3].map((tick) => (
                                        <line
                                            key={tick}
                                            x1="24"
                                            x2="536"
                                            y1={24 + tick * 48}
                                            y2={24 + tick * 48}
                                            className="sa-grid-line"
                                        />
                                    ))}
                                    <path d={billingArea} fill="url(#saBillingArea)" />
                                    <polyline
                                        points={billingPrimaryPoints}
                                        className={`sa-line-primary ${selectedBillingSeries === 'primary' ? 'sa-line-series-selected' : ''}`}
                                    />
                                    {showInternalBillingDetails && (
                                        <polyline
                                            points={billingSecondaryPoints}
                                            className={`sa-line-secondary ${selectedBillingSeries === 'secondary' ? 'sa-line-series-selected' : ''}`}
                                        />
                                    )}
                                    <polyline
                                        points={billingPrimaryPoints}
                                        className="sa-line-hit-area"
                                        tabIndex={0}
                                        role="button"
                                        aria-label={showInternalBillingDetails ? 'Select customer charge series' : 'Select LLM cost series'}
                                        onClick={() => setSelectedBillingSeries('primary')}
                                        onKeyDown={(event) =>
                                        {
                                            if (event.key === 'Enter' || event.key === ' ')
                                            {
                                                event.preventDefault();
                                                setSelectedBillingSeries('primary');
                                            }
                                        }}
                                    />
                                    {showInternalBillingDetails && (
                                        <polyline
                                            points={billingSecondaryPoints}
                                            className="sa-line-hit-area"
                                            tabIndex={0}
                                            role="button"
                                            aria-label="Select provider cost series"
                                            onClick={() => setSelectedBillingSeries('secondary')}
                                            onKeyDown={(event) =>
                                            {
                                                if (event.key === 'Enter' || event.key === ' ')
                                                {
                                                    event.preventDefault();
                                                    setSelectedBillingSeries('secondary');
                                                }
                                            }}
                                        />
                                    )}
                                    {billingTrend.map((point, index) =>
                                    {
                                        const x = 24 + (512 * index) / Math.max(billingTrend.length - 1, 1);
                                        const maxValue = Math.max(...billingTrend.map((item) => item.primary), 1);
                                        const y = 196 - (172 * point.primary) / maxValue;
                                        const isSelected = selectedBillingPoint?.label === point.label;
                                        return (
                                            <circle
                                                key={point.label}
                                                cx={x}
                                                cy={y}
                                                r={isSelected ? 6 : 4}
                                                className={`sa-line-dot ${isSelected ? 'sa-line-dot-selected' : ''}`}
                                                tabIndex={0}
                                                role="button"
                                                aria-label={showInternalBillingDetails
                                                    ? `${point.label} customer charge ${formatCurrency(point.primary)} and provider cost ${formatCurrency(point.secondary)}`
                                                    : `${point.label} LLM cost ${formatCurrency(point.primary)}`}
                                                onClick={() => setSelectedBillingLabel(point.label)}
                                                onKeyDown={(event) =>
                                                {
                                                    if (event.key === 'Enter' || event.key === ' ')
                                                    {
                                                        event.preventDefault();
                                                        setSelectedBillingLabel(point.label);
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </svg>
                                <div className="sa-axis-labels">
                                    {billingTrend.map((point) => (
                                        <button
                                            key={point.label}
                                            type="button"
                                            className={`sa-axis-pill ${selectedBillingPoint?.label === point.label ? 'sa-axis-pill-selected' : ''}`}
                                            onClick={() => setSelectedBillingLabel(point.label)}
                                        >
                                            {point.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="sa-line-legend">
                                    <button
                                        type="button"
                                        className={`sa-line-legend-button ${selectedBillingSeries === 'primary' ? 'sa-line-legend-button-selected' : ''}`}
                                        onClick={() => setSelectedBillingSeries('primary')}
                                    ><i className="sa-line-legend-dot sa-line-legend-primary" />{showInternalBillingDetails ? 'Customer Charge' : 'LLM Cost'}</button>
                                    {showInternalBillingDetails && (
                                        <button
                                            type="button"
                                            className={`sa-line-legend-button ${selectedBillingSeries === 'secondary' ? 'sa-line-legend-button-selected' : ''}`}
                                            onClick={() => setSelectedBillingSeries('secondary')}
                                        ><i className="sa-line-legend-dot sa-line-legend-secondary" />Provider Cost</button>
                                    )}
                                </div>
                            </div>
                        </article>

                        <article className="sa-chart-card">
                            <div className="sa-chart-header">
                                <h2>User Growth (weekly)</h2>
                            </div>
                            {selectedGrowthPoint && (
                                <div className="sa-chart-selection-card">
                                    <strong>{selectedGrowthPoint.label}</strong>
                                    <span>
                                        {selectedGrowthSeries === 'primary'
                                            ? `Active users ${formatCompactNumber(selectedGrowthPoint.primary)}`
                                            : `New users ${formatCompactNumber(selectedGrowthPoint.secondary)}`}
                                    </span>
                                    <small>
                                        {selectedGrowthSeries === 'primary'
                                            ? `New users ${formatCompactNumber(selectedGrowthPoint.secondary)}`
                                            : `Active users ${formatCompactNumber(selectedGrowthPoint.primary)}`}
                                    </small>
                                </div>
                            )}
                            <div className="sa-line-chart-wrap">
                                <svg className="sa-line-chart" viewBox="0 0 560 220" role="img" aria-label="Weekly user growth chart">
                                    <defs>
                                        <linearGradient id="saGrowthArea" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="rgba(31, 169, 124, 0.22)" />
                                            <stop offset="100%" stopColor="rgba(31, 169, 124, 0.03)" />
                                        </linearGradient>
                                    </defs>
                                    {[0, 1, 2, 3].map((tick) => (
                                        <line
                                            key={tick}
                                            x1="24"
                                            x2="536"
                                            y1={24 + tick * 48}
                                            y2={24 + tick * 48}
                                            className="sa-grid-line"
                                        />
                                    ))}
                                    <path d={growthArea} fill="url(#saGrowthArea)" />
                                    <polyline
                                        points={growthPrimaryPoints}
                                        className={`sa-line-growth ${selectedGrowthSeries === 'primary' ? 'sa-line-series-selected' : ''}`}
                                    />
                                    <polyline
                                        points={growthSecondaryPoints}
                                        className={`sa-line-new-users ${selectedGrowthSeries === 'secondary' ? 'sa-line-series-selected' : ''}`}
                                    />
                                    <polyline
                                        points={growthPrimaryPoints}
                                        className="sa-line-hit-area"
                                        tabIndex={0}
                                        role="button"
                                        aria-label="Select active users series"
                                        onClick={() => setSelectedGrowthSeries('primary')}
                                        onKeyDown={(event) =>
                                        {
                                            if (event.key === 'Enter' || event.key === ' ')
                                            {
                                                event.preventDefault();
                                                setSelectedGrowthSeries('primary');
                                            }
                                        }}
                                    />
                                    <polyline
                                        points={growthSecondaryPoints}
                                        className="sa-line-hit-area"
                                        tabIndex={0}
                                        role="button"
                                        aria-label="Select new users series"
                                        onClick={() => setSelectedGrowthSeries('secondary')}
                                        onKeyDown={(event) =>
                                        {
                                            if (event.key === 'Enter' || event.key === ' ')
                                            {
                                                event.preventDefault();
                                                setSelectedGrowthSeries('secondary');
                                            }
                                        }}
                                    />
                                    {userGrowth.map((point, index) =>
                                    {
                                        const x = 24 + (512 * index) / Math.max(userGrowth.length - 1, 1);
                                        const maxValue = Math.max(...userGrowth.map((item) => item.primary), 1);
                                        const y = 196 - (172 * point.primary) / maxValue;
                                        const isSelected = selectedGrowthPoint?.label === point.label;
                                        return (
                                            <circle
                                                key={point.label}
                                                cx={x}
                                                cy={y}
                                                r={isSelected ? 6 : 4}
                                                className={`sa-line-dot sa-line-dot-growth ${isSelected ? 'sa-line-dot-selected' : ''}`}
                                                tabIndex={0}
                                                role="button"
                                                aria-label={`${point.label} active users ${formatCompactNumber(point.primary)} and new users ${formatCompactNumber(point.secondary)}`}
                                                onClick={() => setSelectedGrowthLabel(point.label)}
                                                onKeyDown={(event) =>
                                                {
                                                    if (event.key === 'Enter' || event.key === ' ')
                                                    {
                                                        event.preventDefault();
                                                        setSelectedGrowthLabel(point.label);
                                                    }
                                                }}
                                            />
                                        );
                                    })}
                                </svg>
                                <div className="sa-axis-labels">
                                    {userGrowth.map((point) => (
                                        <button
                                            key={point.label}
                                            type="button"
                                            className={`sa-axis-pill ${selectedGrowthPoint?.label === point.label ? 'sa-axis-pill-selected' : ''}`}
                                            onClick={() => setSelectedGrowthLabel(point.label)}
                                        >
                                            {point.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="sa-line-legend">
                                    <button
                                        type="button"
                                        className={`sa-line-legend-button ${selectedGrowthSeries === 'primary' ? 'sa-line-legend-button-selected' : ''}`}
                                        onClick={() => setSelectedGrowthSeries('primary')}
                                    ><i className="sa-line-legend-dot sa-line-legend-growth" />Active Users</button>
                                    <button
                                        type="button"
                                        className={`sa-line-legend-button ${selectedGrowthSeries === 'secondary' ? 'sa-line-legend-button-selected' : ''}`}
                                        onClick={() => setSelectedGrowthSeries('secondary')}
                                    ><i className="sa-line-legend-dot sa-line-legend-new-users" />New Users</button>
                                </div>
                            </div>
                        </article>
                    </section>

                    {!isOrganizationScope && filterOrgId === null && (
                        <section className="sa-chart-grid">
                            <article className="sa-chart-card sa-chart-card-wide">
                                <div className="sa-chart-header sa-chart-header-row">
                                    <h2>Top Organizations</h2>
                                    <div className="sa-analytics-range-switcher" aria-label="Comparison metric">
                                        {COMPARISON_METRIC_OPTIONS.map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`sa-range-pill ${comparisonMetric === option.value ? 'sa-range-pill-active' : ''}`}
                                                onClick={() => setComparisonMetric(option.value)}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {dynamicSeries.comparison && dynamicSeries.comparison.items.length > 0 ? (
                                    <div className="sa-bar-chart">
                                        {(() =>
                                        {
                                            const rows = dynamicSeries.comparison!.items;
                                            const maxValue = Math.max(...rows.map((row) => row.value), 1);
                                            const formatter = (value: number): string =>
                                                comparisonMetric === 'spend'
                                                    ? formatCurrency(value)
                                                    : formatCompactNumber(value);
                                            return rows.map((row) => (
                                                <button
                                                    key={row.organization_id}
                                                    type="button"
                                                    className={`sa-bar-row ${selectedOrgId === row.organization_id ? 'sa-bar-row-selected' : ''}`}
                                                    onClick={() =>
                                                    {
                                                        setSelectedOrgId(row.organization_id);
                                                        setSelectedOrganizationName(row.organization_name);
                                                    }}
                                                >
                                                    <div className="sa-bar-label">{row.organization_name}</div>
                                                    <svg className="sa-bar-track" viewBox="0 0 100 14" preserveAspectRatio="none" role="presentation">
                                                        <rect className="sa-bar-track-bg" x="0" y="0" width="100" height="14" rx="4" ry="4" />
                                                        <rect
                                                            className={`sa-bar-fill ${selectedOrgId === row.organization_id ? 'sa-bar-fill-selected' : ''}`}
                                                            x="0"
                                                            y="0"
                                                            width={clampRatio(row.value / maxValue) * 100}
                                                            height="14"
                                                            rx="4"
                                                            ry="4"
                                                        />
                                                    </svg>
                                                    <div className="sa-bar-value">{formatter(row.value)}</div>
                                                </button>
                                            ));
                                        })()}
                                    </div>
                                ) : (
                                    <div className="sa-analytics-caption">No comparison data available for the selected window.</div>
                                )}
                            </article>
                        </section>
                    )}
                </>
            )}

            <div className="sa-analytics-caption">
                <BarChartLine size={15} />
                {caption}
            </div>
        </div>
    );
}

export default SuperAdminAnalytics;
